/**
 * Cutting a live Word range into its words: one call for every Word-side reader,
 * each naming the characters it cuts at.
 *
 * WHY the separators are the caller's
 *   The comment anchors cut at the space alone, and a review comment placed on a
 *   paragraph's first word has always landed on "4.<tab>Fees" in "4.<tab>Fees and
 *   Payment"; that is production behaviour and stays. The cell writer cuts at every
 *   character the tokenizer treats as whitespace, so Word's ranges line up with
 *   the tokenizer's words.
 *
 * Each range's text with every tracked change accepted (`queueAcceptedTextsOf`) is
 * what places it on a paragraph's accepted text (`AcceptedTextWordRanges`).
 */

/**
 * The characters the tokenizer (`WordTokenizer`) cuts words at, for a caller whose
 * Word ranges must line up with the tokenizer's words: a space, a tab, a
 * non-breaking space, the vertical tab Word reads a manual line break as, and the
 * two line-end characters.
 *
 * EXAMPLE
 *   "Implementation<tab>services"  -> two ranges, "Implementation" and "services", as the tokenizer reads two words
 */
export const WORD_SEPARATORS = [" ", "\t", " ", "\u000b", "\n", "\r"];

/**
 * The words of `range` as Word ranges, cut at `separators` and each trimmed to the
 * word itself, queued and loaded so the batch's next `sync` answers them together
 * with everything else queued on the context.
 */
export function queueWordRangesOf(range: Word.Range, separators: string[]): Word.RangeCollection {
    const wordRanges = range.getTextRanges(separators, true);
    wordRanges.load("items");
    return wordRanges;
}

/** Word's `ChangeTrackingVersion` for the text with every tracked change accepted, in its string form. */
const EVERY_CHANGE_ACCEPTED = "Current";

/**
 * The text Word reads in each of `wordRanges` once every tracked change is accepted,
 * queued so the batch's next `sync` answers them all: "" for a range that stands
 * inside a tracked deletion.
 *
 * EXAMPLE
 *   ranges "shall", "be", …, "($250,000)", "not" over Laura's deletion of "be limited to … ($250,000)"
 *   -> "shall", "", …, "", "not"
 */
export function queueAcceptedTextsOf(wordRanges: Word.RangeCollection): OfficeExtension.ClientResult<string>[] {
    return wordRanges.items.map((wordRange) => wordRange.getReviewedText(EVERY_CHANGE_ACCEPTED));
}
